import { useMemo } from "react";
import { ItemUI } from "../types/item";
import { User } from "../types/user";

export type SplitResult = {
  subtotal: number;
  tax: number;
  tip: number;
};

// One person's share of a single item: price * (their shares / total splits).
function userItemAmount(item: ItemUI, userId: string): number {
  const splits = item.splits > 0 ? item.splits : 1;
  const shares = item.shares[userId] ?? 0;
  return shares > 0 ? (item.price * shares) / splits : 0;
}

export function useSplitCalc(
  items: ItemUI[],
  tax: number,
  tip: number,
  user: User | null
): SplitResult {
  return useMemo(() => {
    if (user === null) return { subtotal: 0, tax: 0, tip: 0 };

    const totalSum = items.reduce((sum, item) => sum + item.price, 0);

    let subtotal = 0;
    for (const item of items) {
      subtotal += userItemAmount(item, user.id);
    }

    // tax/tip split by how much of the whole check this person covers; unclaimed
    // portions drop their share of tax/tip too (they're left unassigned).
    const ratio = totalSum > 0 ? subtotal / totalSum : 0;

    return {
      subtotal,
      tax: tax * ratio,
      tip: tip * ratio,
    };
  }, [items, tax, tip, user]);
}

// The portion of the check no one has claimed yet: for each item, the fraction
// of its price whose shares are still unassigned. Left unassigned by design, so
// the owner can chase it. Includes the tax/tip that rides on that fraction.
export function useUnclaimed(items: ItemUI[], tax: number, tip: number): number {
  return useMemo(() => {
    const totalSum = items.reduce((sum, item) => sum + item.price, 0);

    let unclaimedSubtotal = 0;
    for (const item of items) {
      const splits = item.splits > 0 ? item.splits : 1;
      const claimed = Object.values(item.shares).reduce((a, b) => a + b, 0);
      const remaining = Math.max(0, splits - claimed);
      unclaimedSubtotal += (item.price * remaining) / splits;
    }

    const ratio = totalSum > 0 ? unclaimedSubtotal / totalSum : 0;
    return unclaimedSubtotal + (tax + tip) * ratio;
  }, [items, tax, tip]);
}
